import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useDebouncedValue } from '@/features/self-service/hooks/use-debounced-value';
import { formatBRL } from '@/lib/currency';
import { cn } from '@/lib/utils';
import type { SearchResultModel } from '@/features/self-service/utils/search-results';
import { useStorefrontAutocomplete } from '../hooks/use-storefront-autocomplete';
import { STOREFRONT_EVENT, trackStorefront } from '../utils/analytics';
import { CATALOG_ALL_PATH, productPath } from '../utils/slugs';
import { ProductThumb } from './ProductThumb';

/** Mesma cadência da busca da listagem. */
const DEBOUNCE_MS = 300;

const LISTBOX_ID = 'vitrine-sugestoes';
const optionId = (index: number) => `${LISTBOX_ID}-opt-${index}`;

/**
 * Busca de produtos do topo, com sugestões.
 *
 * Fica entre a marca e as ações da `Topbar`, **só no ramo da vitrine** — o
 * self-service tem layout próprio e não passa por aqui.
 *
 * Enter sem sugestão destacada leva à listagem com o termo na URL, que é onde
 * existem paginação e filtros. O painel é atalho, não substituto dela.
 */
export function StorefrontSearchAutocomplete() {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  // Separado do `open` de propósito: `Escape` fecha o painel mas o cursor
  // continua no campo, e o overlay tem de continuar de pé enquanto isso.
  const [focused, setFocused] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounced = useDebouncedValue(value, DEBOUNCE_MS);

  // Fechado não consulta: o painel some, mas a query continuaria viva a cada
  // tecla se o termo seguisse entrando no hook.
  const { suggestions, found, isFetching, isIdle, isUnavailable } =
    useStorefrontAutocomplete(open ? debounced : '');

  // Termo novo, destaque zerado. Sem isto o índice antigo aponta para outra
  // peça e o Enter abre um produto que a pessoa não chegou a ver.
  useEffect(() => setHighlight(-1), [debounced]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  function go(to: string) {
    setOpen(false);
    setValue('');
    // `focused` também, e não só `open`: é ele que sustenta o overlay, e clicar
    // numa sugestão NAVEGA — sai da busca inteira, não apenas fecha o painel.
    // Sem isto o escurecimento ficava por cima da página nova até alguém clicar
    // nele.
    //
    // O `blur()` no DOM junto: com o cursor ainda no campo, o estado e o
    // elemento discordariam, e a próxima tecla abriria o painel sem overlay.
    setFocused(false);
    inputRef.current?.blur();
    navigate(to);
  }

  /**
   * `source` distingue os três caminhos que caem na listagem com o termo: a
   * tecla Enter sem sugestão destacada, o rodapé "Ver todos os N", e o modelo
   * sem grade que não tem página para abrir. Colapsá-los faria o rodapé — que é
   * a decisão deliberada de ver a lista inteira — sumir dentro do Enter.
   *
   * O termo NÃO é reemitido pela listagem de destino: ela não tem mais campo de
   * busca — só a pílula que mostra e remove o termo —, e a remoção sai como
   * filtro removido. Uma busca do topo é um evento, não dois.
   */
  function submitTerm(source: string) {
    const term = value.trim();
    if (term.length === 0) return;
    trackStorefront(STOREFRONT_EVENT.searchSubmitted, {
      term,
      term_length: term.length,
      source,
      suggestions_count: suggestions.length,
      results_total: found,
    });
    go(`${CATALOG_ALL_PATH}?q=${encodeURIComponent(term)}`);
  }

  function openSuggestion(model: SearchResultModel, index: number) {
    const productId = model.grades[0]?.productId;
    // Um modelo sem grade não tem página para abrir. Cai na listagem com o
    // termo em vez de navegar para `/produto/undefined`.
    if (!productId) return submitTerm('autocomplete_sem_grade');
    trackStorefront(STOREFRONT_EVENT.searchSuggestionSelected, {
      term: value.trim(),
      position: index,
      product_id: productId,
      model_key: model.key,
      brand: model.brand,
      sale_price: model.salePrice,
      results_total: found,
    });
    go(productPath(productId));
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const picked = suggestions[highlight];
      if (picked) openSuggestion(picked, highlight);
      else submitTerm('autocomplete_enter');
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    if (suggestions.length === 0) return;
    event.preventDefault();
    setOpen(true);
    const step = event.key === 'ArrowDown' ? 1 : -1;
    // São `length + 1` estados, porque -1 ("nada destacado") é um deles e a
    // volta passa por ele — descer do último devolve o foco ao campo em vez de
    // pular para o primeiro. O `+1` desloca -1..n-1 para 0..n antes do módulo;
    // o `+ length + 1` é o que mantém o resto positivo na seta para cima.
    const estados = suggestions.length + 1;
    setHighlight(((highlight + 1 + step + estados) % estados) - 1);
  }

  const showPanel = open && value.trim().length > 0;

  return (
    // Recolhida em repouso, abre ao receber foco. `focus-within` e não estado
    // em React: a transição é do CSS, então digitar não re-renderiza a barra
    // inteira a cada tecla só para manter a largura.
    //
    // Os dois sentidos têm tempos DIFERENTES, e é o que faz o movimento
    // parecer natural: vale sempre a transição declarada no estado de destino,
    // então o `focus-within:duration-[420ms]` rege a abertura e o
    // `duration-[110ms]` da base rege o fechamento. Com um valor só, a
    // abertura sai abrupta e o fechamento arrasta na cauda do `ease-out`.
    //
    // Os mesmos 420/110 valem para o overlay, de propósito: os dois se movem
    // juntos, e tempos diferentes fariam a página escurecer fora de compasso
    // com o campo que abre.
    <>
      {/* Escurece a página abaixo do cabeçalho enquanto o campo tem foco.

          PORTAL para o `body`, e é obrigatório: a `Topbar` tem `backdrop-blur`,
          e `backdrop-filter` torna o elemento bloco de contenção dos
          descendentes `fixed` — igual a `transform` e `filter`. Renderizado
          aqui dentro, o overlay se posicionaria pelo header de 64px e
          `top-16 bottom-0` lhe daria altura zero. Também resolve o empilhamento:
          no `body` ele fica sob a `Topbar` (`z-40`) e sobre o conteúdo.

          Fica FORA do `rootRef` de propósito: dentro dele, o `pointerdown` que
          fecha o painel leria o clique no overlay como clique "dentro" e nada
          fecharia. O portal garante isso por construção.

          `top-28 sm:top-16` é a altura da `Topbar` nos dois arranjos dela — no
          celular ela tem duas linhas, porque a busca ocupa a segunda inteira.
          Os dois números são um PAR com o `h-28 sm:h-16` de lá: mudar a altura
          do cabeçalho sem mudar isto faz o scrim começar em cima do próprio
          campo que está com foco.

          O recorte é deliberado: o
          cabeçalho fica INTEIRO aceso e não anima nada. Cobrir a tela toda
          obrigava a barra a subir de `z`, apagar marca e ações e reanimar tudo
          isso — movimento que ficou pior que o problema que resolvia.

          `bg-black/50 backdrop-blur-sm` é o mesmo scrim do `Sheet`, e não uma
          cor nova: um token de tema não serviria aqui, porque `background` é
          claro no light e escuraria só no dark.

          Sempre montado, alternando opacidade — desmontar mataria a saída, que
          aqui é o sentido mais curto (110ms contra 420ms), igual à largura do
          campo. */}
      {createPortal(
        <div
          aria-hidden
          onMouseDown={() => setFocused(false)}
          className={cn(
            'fixed inset-x-0 bottom-0 top-28 z-30 bg-black/50 backdrop-blur-sm transition-opacity ease-out sm:top-16',
            focused
              ? 'opacity-100 duration-[420ms]'
              : 'pointer-events-none opacity-0 duration-[110ms]'
          )}
        />,
        document.body
      )}
      {/* O campo cresce no foco a partir de `sm`, e NÃO no celular.

          A animação existe porque lá ele divide a linha com a marca e as
          ações: nasce em 15rem para não empurrá-las e abre para `max-w-md`
          quando alguém vai digitar. No celular ele tem a linha inteira desde
          que o menu tirou os outros botões da barra — capá-lo em 15rem
          deixaria 240px de campo num espaço de 327px, encostado à esquerda, e
          o foco daria um salto de largura sem motivo. */}
      <div
        ref={rootRef}
        className="relative w-full sm:max-w-[15rem] sm:transition-[max-width] sm:duration-[110ms] sm:ease-out sm:focus-within:max-w-md sm:focus-within:duration-[420ms]"
      >
        <Label htmlFor="vitrine-busca-topo" className="sr-only">
          Buscar peças
        </Label>
        <MagnifyingGlass
          size={18}
          aria-hidden
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          id="vitrine-busca-topo"
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={showPanel}
          aria-controls={LISTBOX_ID}
          aria-autocomplete="list"
          aria-activedescendant={
            highlight >= 0 ? optionId(highlight) : undefined
          }
          autoComplete="off"
          value={value}
          placeholder="Buscar por marca, peça ou cor"
          onChange={(event) => {
            setValue(event.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            setFocused(true);
            setOpen(true);
          }}
          onBlur={() => setFocused(false)}
          onKeyDown={onKeyDown}
          // Preenchimento suave no lugar da borda: `border-input` sólida pesa
          // demais para um campo que fica sempre na tela.
          // `h-11` fica: são os 44px de alvo que o app inteiro respeita.
          //
          // Repouso → hover → foco é uma rampa de opacidade do MESMO `muted`
          // (60 → 80 → 100), e não uma troca de token: `muted`, `secondary` e
          // `accent` valem a mesma cor no tema, então não existe um passo neutro
          // mais escuro para onde ir. No foco o campo fica mais denso que o
          // fundo, em vez de virar branco.
          //
          // O foco global (`*:focus-visible` em `globals.css`) aplica
          // `outline: 4px` E `border-radius: 4px` — o raio quadrava a pílula no
          // clique. Aqui os dois saem: quem sinaliza o foco é o overlay que
          // escurece a página inteira, mais o fundo cheio e a largura que abre.
          //
          // É a alternativa visível que o guia do clearance exige junto do
          // `outline-none` — e mais forte que o contorno, porque some tudo em
          // volta em vez de desenhar uma linha a mais.
          // `hover:border-transparent` e `font-normal` desfazem, aqui, dois
          // padrões que o `Input` passou a trazer: o contorno que aparece no
          // hover e o valor em `semibold`. Os dois servem a campo de
          // formulário — este é uma busca de topo, sem borda e sem rótulo, e a
          // borda acendendo no hover desenharia justamente a linha que o
          // preenchimento existe para evitar.
          //
          // `hover:border-transparent` com o modificador, e não só
          // `border-transparent`: o `tailwind-merge` só derruba classe da
          // mesma variante, então a sem modificador não alcança a de hover.
          className="rounded-full border-transparent bg-muted/60 pl-10 text-sm font-normal hover:border-transparent hover:bg-muted/80 focus-visible:rounded-full focus-visible:bg-muted focus-visible:outline-none"
        />

        {showPanel && (
          <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-md border border-border bg-background shadow-lg">
            {/* `aria-busy` e não um esqueleto: a lista anterior segue na tela
              enquanto a nova chega, então trocá-la por blocos cinzas faria o
              painel piscar a cada tecla. */}
            <ul id={LISTBOX_ID} role="listbox" aria-busy={isFetching}>
              {suggestions.map((model, index) => (
                <li key={model.key}>
                  <SuggestionRow
                    id={optionId(index)}
                    model={model}
                    active={index === highlight}
                    onPick={() => openSuggestion(model, index)}
                    onHover={() => setHighlight(index)}
                  />
                </li>
              ))}
            </ul>
            <PanelFooter
              isIdle={isIdle}
              isUnavailable={isUnavailable}
              isFetching={isFetching}
              count={suggestions.length}
              found={found}
              onSeeAll={() => submitTerm('autocomplete_ver_todos')}
            />
          </div>
        )}
      </div>
    </>
  );
}

function SuggestionRow({
  id,
  model,
  active,
  onPick,
  onHover,
}: {
  id: string;
  model: SearchResultModel;
  active: boolean;
  onPick: () => void;
  onHover: () => void;
}) {
  return (
    <button
      id={id}
      type="button"
      role="option"
      aria-selected={active}
      // `onMouseDown` e não `onClick`: o `pointerdown` que fecha o painel
      // dispara antes do clique e a linha sumiria debaixo do cursor.
      onMouseDown={(event) => {
        event.preventDefault();
        onPick();
      }}
      onMouseEnter={onHover}
      className={cn(
        'flex w-full items-center gap-3 px-3 py-2 text-left transition-colors',
        active ? 'bg-accent' : 'hover:bg-accent/60'
      )}
    >
      <ProductThumb
        urls={model.assetsUrls}
        notes={model.assetsNotes}
        category={model.category}
        alt=""
        className="w-10 shrink-0"
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-foreground">
          {model.description}
        </span>
        <span className="block truncate text-xs text-muted-foreground">
          {[model.brand, model.color].filter(Boolean).join(' · ')}
        </span>
      </span>
      <span className="shrink-0 text-sm tabular-nums text-foreground">
        {model.priceVaries && (
          <span className="text-xs text-muted-foreground">a partir de </span>
        )}
        {formatBRL(model.salePrice)}
      </span>
    </button>
  );
}

function PanelFooter({
  isIdle,
  isUnavailable,
  isFetching,
  count,
  found,
  onSeeAll,
}: {
  isIdle: boolean;
  isUnavailable: boolean;
  isFetching: boolean;
  count: number;
  found: number;
  onSeeAll: () => void;
}) {
  const message = isUnavailable
    ? 'Busca indisponível no momento.'
    : isIdle
    ? 'Digite ao menos duas letras.'
    : count === 0 && !isFetching
    ? 'Nenhuma peça encontrada.'
    : null;

  if (message) {
    return (
      <p className="px-3 py-3 text-sm text-muted-foreground" role="status">
        {message}
      </p>
    );
  }
  if (count === 0) return null;

  return (
    <button
      type="button"
      onMouseDown={(event) => {
        event.preventDefault();
        onSeeAll();
      }}
      className="flex min-h-11 w-full items-center justify-center border-t border-border px-3 text-sm font-medium text-foreground transition-colors hover:bg-accent"
    >
      Ver todos os {found} resultados
    </button>
  );
}
